import { Injectable } from "@angular/core";

import {
  ApiAddAction,
  ApiBrowseAction,
  ApiDeleteAction,
  ApiEditAction,
  ApiGenericAction
} from "../../api-actions";
import { ApiStateStoreService } from "../../api-state-store.service";
import { ApiSessionService } from "../../api-session.service";
import { sessionId } from "../../apiParams/session-id";
import { ParameterValue } from "../../util/api-action-description";
import { ApiResourceService } from "../api-resource.service";
import { extractData, OnsipApiResponse } from "../../apiResponse/response-body-new";
import { getApiActionName } from "../../onsip-api-action-new";
import {
  ApiPhone,
  apiPhoneToPhone as clean,
  Phone,
  PhoneAddRequestParams,
  PhoneAssignParams,
  PhoneDeassignParams,
  PhoneEditRequestParams
} from "./phone";
import { organizationId } from "../../apiParams/organization-id";
import { arrayToRecord } from "../../util/arrayToRecord";
import { onsipApiArrayToArray } from "../../apiResponse/xml-json";
import { ApiPromiseState, ApiPromiseStateService } from "../../api-promise-state.service";
export { Phone };

const debug = false;

@Injectable({ providedIn: "root" })
export class PhoneService extends ApiResourceService<Phone> {
  constructor(
    session: ApiSessionService,
    store: ApiStateStoreService,
    promiseState: ApiPromiseStateService
  ) {
    super(session, store, promiseState, "Phone", "phoneId");
    debug && this.state.subscribe(state => console.warn("PhoneService", state));
  }

  phoneBrowse(extraParams: Record<string, ParameterValue> = {}): ApiPromiseState<Phone> {
    debug && console.log("PhoneService.phoneBrowse");
    this.dispatcher.next({
      parameters: {
        Action: ApiBrowseAction.PhoneBrowse,
        SessionId: this.store.state.pipe(sessionId()),
        Limit: 2500, // TODO paginate correctly
        OrganizationId: this.store.state.pipe(organizationId()),
        ...extraParams
      }
    });
    return this.promiseState.toPromise(ApiBrowseAction.PhoneBrowse);
  }

  phoneEdit(params: PhoneEditRequestParams): ApiPromiseState<Phone> {
    debug && console.log("PhoneService.phoneEdit");
    this.dispatcher.next({
      parameters: {
        Action: ApiEditAction.PhoneEdit,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        ...params
      }
    });
    return this.promiseState.toPromise(ApiEditAction.PhoneEdit);
  }

  phoneAdd(params: PhoneAddRequestParams): ApiPromiseState<Phone> {
    debug && console.log("PhoneService.phoneAdd");
    this.dispatcher.next({
      parameters: {
        Action: ApiAddAction.PhoneAdd,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        ...params
      }
    });
    return this.promiseState.toPromise(ApiAddAction.PhoneAdd);
  }

  phoneDelete(PhoneId: string): ApiPromiseState<Phone> {
    debug && console.log("PhoneService.phoneDelete");
    this.dispatcher.next({
      parameters: {
        Action: ApiDeleteAction.PhoneDelete,
        SessionId: this.store.state.pipe(sessionId()),
        PhoneId
      }
    });
    return this.promiseState.toPromise(ApiDeleteAction.PhoneDelete);
  }

  // not used in the app - use phoneAssignAddressBulk to update assigned addresses instead
  phoneAssignAddress(params: PhoneAssignParams): ApiPromiseState<Phone> {
    debug && console.log("PhoneService.phoneAssignAddress");
    this.dispatcher.next({
      parameters: {
        Action: ApiGenericAction.PhoneAssignAddress,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        ...params
      }
    });
    return this.promiseState.toPromise(ApiGenericAction.PhoneAssignAddress);
  }

  phoneAssignAddressBulk(
    PhoneId: string,
    params: Record<string, ParameterValue>
  ): ApiPromiseState<Phone> {
    debug && console.log("PhoneService.phoneAssignAddressBulk");
    this.dispatcher.next({
      parameters: {
        Action: ApiGenericAction.PhoneAssignAddressBulk,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        PhoneId,
        ...params
      }
    });
    return this.promiseState.toPromise(ApiGenericAction.PhoneAssignAddressBulk);
  }

  phoneDeassignAddress(params: PhoneDeassignParams): ApiPromiseState<Phone> {
    debug && console.log("PhoneService.phoneDeassignAddress");
    this.dispatcher.next({
      parameters: {
        Action: ApiGenericAction.PhoneDeassignAddress,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        ...params
      }
    });
    return this.promiseState.toPromise(ApiGenericAction.PhoneDeassignAddress);
  }

  /** resets phone provision and removes phone from the boot server
   * phone will still work until the phone is reset, but after reset, user will need to do the
   * provisioning steps again
   */
  phoneRemoveEncryptionKey(PhoneId: string): ApiPromiseState<Phone> {
    debug && console.log("PhoneService.phoneRemoveEncryptionKey");
    this.dispatcher.next({
      parameters: {
        Action: ApiGenericAction.PhoneRemoveEncryptionKey,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        PhoneId
      }
    });
    return this.promiseState.toPromise(ApiGenericAction.PhoneRemoveEncryptionKey);
  }

  reducer(response: OnsipApiResponse): void {
    const action = getApiActionName(response);
    switch (action) {
      case ApiBrowseAction.PhoneBrowse:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord(
            extractData<Array<ApiPhone>>(response, action, "Phone", "Phones").map(clean),
            this.indexKeyName
          ),
          action
        );
        break;
      case ApiEditAction.PhoneEdit:
      case ApiAddAction.PhoneAdd:
      case ApiGenericAction.PhoneAssignAddressBulk:
      case ApiGenericAction.PhoneDeassignAddress:
      case ApiGenericAction.PhoneRemoveEncryptionKey:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord(
            [extractData<ApiPhone>(response, action, "Phone")].map(clean),
            this.indexKeyName
          ),
          action
        );
        break;
      case ApiDeleteAction.PhoneDelete:
        if (!response.Context.Request.Parameters) break;
        // eslint-disable-next-line no-case-declarations
        const deletedPhone = onsipApiArrayToArray(
          response.Context.Request.Parameters,
          "Parameter"
        ).find(param => param.Name === "PhoneId")?.Value;
        deletedPhone &&
          this.store.mergeStateUpdate(this.resourceName, { [deletedPhone]: undefined }, action);
        break;
    }
  }
}
